/**
 * Helpers for checkout Amplitude events (#18413).
 */

const COUPON_PREFIX = 'Coupon: ';
const GIFTCARD_PREFIX = 'Giftcard: ';

/**
 * @param {{ symbol?: string }} currency
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export const formatAmplitudeCheckoutSavings = (currency, value) => {
  const sym = currency?.symbol != null ? String(currency.symbol) : '$';
  const n = Number(value);
  if (Number.isNaN(n) || value == null) {
    return `${sym}0.00`;
  }
  return `${sym}${n.toFixed(2)}`;
};

/**
 * Discount lines newly present after apply (GraphQL returns full list).
 * @param {{ title?: string, value?: number|string }[]} [prevDiscounts]
 * @param {{ title?: string, value?: number|string }[]} [nextDiscounts]
 * @returns {{ title: string, value: number }[]}
 */
export const getAddedDiscountLines = (prevDiscounts, nextDiscounts) => {
  const prevTitles = new Set(
    (prevDiscounts || []).map(d => String(d?.title || ''))
  );
  return (nextDiscounts || [])
    .filter(d => d && !prevTitles.has(String(d.title || '')))
    .map(d => ({
      title: String(d.title || ''),
      value: Number(d.value) || 0,
    }));
};

/**
 * Split added lines into coupon vs gift card parts for Amplitude (#18413).
 * @param {{ title: string, value: number }[]} addedLines
 * @returns {{ couponCodes: string[], couponSavings: number, giftCardNumbers: string[], giftCardSavings: number }}
 */
export const splitDiscountLinesForAmplitude = addedLines => {
  const couponCodes = [];
  let couponSavings = 0;
  const giftCardNumbers = [];
  let giftCardSavings = 0;

  addedLines.forEach(({ title, value }) => {
    if (title.startsWith(COUPON_PREFIX)) {
      couponCodes.push(title.slice(COUPON_PREFIX.length).trim());
      couponSavings += value;
    } else if (title.startsWith(GIFTCARD_PREFIX)) {
      giftCardNumbers.push(title.slice(GIFTCARD_PREFIX.length).trim());
      giftCardSavings += value;
    }
  });

  return { couponCodes, couponSavings, giftCardNumbers, giftCardSavings };
};

/** Heuristic: single promo field may be numeric gift card vs alphabetic coupon. */
export const classifyPromoInput = raw => {
  const s = String(raw || '').trim();
  if (!s) {
    return { treatAsGiftCard: false, treatAsCoupon: false };
  }
  if (/^\d+$/.test(s)) {
    return { treatAsGiftCard: true, treatAsCoupon: false };
  }
  return { treatAsGiftCard: false, treatAsCoupon: true };
};
